import { useQuery, type UseQueryResult } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"
import { Role, toRole as parseRole } from "@/lib/roles"

import type { UserId } from "../data/review-data"

/**
 * People sidebar data for a review. Reads GET /api/v1/reviews/{id}/people
 * and adapts the wire shape into the `Colleague[]` list the assignee /
 * reassign / route-to-SME dropdowns expect.
 *
 * Row-scoped: a client-role caller receives `assignable: []` (backend
 * withholds the staff roster from clients per DD-17). We surface the empty
 * list — the workspace UI already knows to hide the assign-to controls
 * when there are no candidates.
 */

// ── Wire types (mirror schemas/assignment.py) ────────────────────────

interface PersonOutWire {
  id: number
  email: string
  full_name: string | null
  role: string
}

interface ReviewPeopleOutWire {
  review_id: number
  review_stage: string
  secondary_skipped: boolean
  archived: boolean
  owner: PersonOutWire | null
  assignee: PersonOutWire | null
  client_id: number | null
  client_name: string | null
  client_contacts: PersonOutWire[]
  primary_client_contact_id: number | null
  assignable: PersonOutWire[]
}

// ── Adapter ─────────────────────────────────────────────────────────

export interface Colleague {
  userId: UserId
  name: string
  role: Role
}

/**
 * Map any wire role into the review's `Role` type. Unknown → viewer (safest
 * read-only bucket rather than silently promoting).
 */
function toRole(role: string): Role {
  return parseRole(role) ?? Role.VIEWER
}

function toColleague(p: PersonOutWire): Colleague {
  return {
    userId: String(p.id) as UserId,
    name: p.full_name ?? p.email,
    role: toRole(p.role),
  }
}

export interface ReviewPeople {
  assignee: Colleague | null
  owner: Colleague | null
  clientName: string | null
  clientContacts: Colleague[]
  assignable: Colleague[]
}

async function fetchReviewPeople(reviewId: string, signal: AbortSignal): Promise<ReviewPeople> {
  const wire = await http.get<ReviewPeopleOutWire>(
    `/api/v1/reviews/${encodeURIComponent(reviewId)}/people`,
    { signal },
  )
  return {
    assignee: wire.assignee ? toColleague(wire.assignee) : null,
    owner: wire.owner ? toColleague(wire.owner) : null,
    clientName: wire.client_name,
    clientContacts: wire.client_contacts.map(toColleague),
    assignable: wire.assignable.map(toColleague),
  }
}

export function useReviewPeopleQuery(reviewId: string | null): UseQueryResult<ReviewPeople> {
  return useQuery({
    queryKey: reviewId != null ? qk.reviews.people(reviewId) : ["reviews", "people", "none"],
    queryFn: ({ signal }) => fetchReviewPeople(reviewId as string, signal),
    enabled: reviewId != null,
  })
}
